<template>
  <div class="error-book">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">错题本</h1>
        <div class="header-stats">
          <div class="stat-item">
            <span class="stat-number">{{ totalErrors }}</span>
            <span class="stat-label">总错题</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ masteredErrors }}</span>
            <span class="stat-label">已掌握</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ unmasteredErrors }}</span>
            <span class="stat-label">未掌握</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 筛选和操作栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <!-- 学科筛选 -->
        <el-select v-model="selectedSubject" placeholder="选择学科" @change="handleSubjectChange">
          <el-option label="全部学科" value=""></el-option>
          <el-option 
            v-for="subject in subjects" 
            :key="subject.value" 
            :label="subject.label" 
            :value="subject.value">
          </el-option>
        </el-select>

        <!-- 知识点筛选 -->
        <el-select v-model="selectedKnowledge" placeholder="选择知识点" @change="handleKnowledgeChange">
          <el-option label="全部知识点" value=""></el-option>
          <el-option 
            v-for="knowledge in knowledgePoints" 
            :key="knowledge.value" 
            :label="knowledge.label" 
            :value="knowledge.value">
          </el-option>
        </el-select>

        <!-- 掌握状态筛选 -->
        <el-select v-model="selectedStatus" placeholder="掌握状态" @change="handleStatusChange">
          <el-option label="全部状态" value=""></el-option>
          <el-option label="已掌握" value="mastered"></el-option>
          <el-option label="未掌握" value="unmastered"></el-option>
        </el-select>

        <!-- 时间筛选 -->
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          @change="handleDateChange">
        </el-date-picker>
      </div>

      <div class="filter-right">
        <!-- 批量操作 -->
        <el-button 
          v-if="selectedErrorIds.length > 0" 
          type="success" 
          size="small"
          @click="batchMarkMastered">
          批量标记已掌握
        </el-button>
        
        <el-button 
          v-if="selectedErrorIds.length > 0" 
          type="primary" 
          size="small"
          @click="batchPrint">
          批量打印
        </el-button>

        <!-- 视图切换 -->
        <div class="view-toggle">
          <el-button-group>
            <el-button 
              :type="viewMode === 'grid' ? 'primary' : ''" 
              size="small"
              @click="viewMode = 'grid'">
              <i class="fa fa-th"></i>
            </el-button>
            <el-button 
              :type="viewMode === 'list' ? 'primary' : ''" 
              size="small"
              @click="viewMode = 'list'">
              <i class="fa fa-list"></i>
            </el-button>
          </el-button-group>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-text">
        <i class="el-icon-loading"></i>
        正在加载错题数据...
      </div>
    </div>

    <!-- 错题列表 - 网格视图 -->
    <div v-else-if="viewMode === 'grid'" class="error-grid">
      <div 
        v-for="error in errorList" 
        :key="error.id"
        class="error-card"
        :class="{ 
          selected: selectedErrorIds.includes(error.id),
          mastered: error && error.status === 'mastered'
        }"
        @click="selectError(error)"
      >
        <!-- 选择框 -->
        <div class="card-checkbox" @click.stop="toggleSelection(error.id)">
          <el-checkbox v-model="selectedErrorIds" :label="error.id"></el-checkbox>
        </div>

        <!-- 错题图片 -->
        <div class="error-image">
          <img :src="error.imageUrl" :alt="error.subject" @click.stop="viewImage(error.imageUrl)" />
          <div class="image-overlay">
            <i class="fa fa-search-plus"></i>
          </div>
        </div>

        <!-- 错题信息 -->
        <div class="error-info">
          <div class="error-subject">{{ error.subject }}</div>
          <div class="error-knowledge">{{ error.knowledgePoint }}</div>
          <div class="error-time">{{ formatTime(error.createTime) }}</div>
          
          <!-- 状态标签 -->
          <div class="error-status" :class="error && error.status ? error.status : 'unmastered'">
            {{ error && error.status === 'mastered' ? '已掌握' : '未掌握' }}
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="error-actions">
          <el-button size="mini" type="text" @click.stop="viewDetail(error)">
            <i class="fa fa-eye"></i> 查看
          </el-button>
          <el-button size="mini" type="text" @click.stop="practiceAgain(error)">
            <i class="fa fa-redo"></i> 重做
          </el-button>
          <el-button 
            size="mini" 
            type="text" 
            :class="error && error.status === 'mastered' ? 'text-warning' : 'text-success'"
            @click.stop="toggleMastered(error)">
            <i class="fa" :class="error && error.status === 'mastered' ? 'fa-times' : 'fa-check'"></i>
            {{ error && error.status === 'mastered' ? '取消掌握' : '标记掌握' }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 错题列表 - 列表视图 -->
    <div v-else-if="viewMode === 'list'" class="error-list">
      <el-table 
        :data="errorList" 
        @selection-change="handleSelectionChange"
        @row-click="viewDetail">
        <el-table-column type="selection" width="55"></el-table-column>
        
        <el-table-column label="题目图片" width="120">
          <template slot-scope="scope">
            <div class="table-image" @click.stop="viewImage(scope.row.imageUrl)">
              <img :src="scope.row.imageUrl" :alt="scope.row.subject" />
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="subject" label="学科" width="80"></el-table-column>
        <el-table-column prop="knowledgePoint" label="知识点" width="150"></el-table-column>
        
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag 
              :type="scope.row && scope.row.status === 'mastered' ? 'success' : 'danger'"
              size="small">
              {{ scope.row && scope.row.status === 'mastered' ? '已掌握' : '未掌握' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="createTime" label="添加时间" width="150">
          <template slot-scope="scope">
            {{ formatTime(scope.row.createTime) }}
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="200">
          <template slot-scope="scope">
            <el-button size="mini" @click.stop="viewDetail(scope.row)">查看</el-button>
            <el-button size="mini" type="primary" @click.stop="practiceAgain(scope.row)">重做</el-button>
            <el-button 
              size="mini" 
              :type="scope.row && scope.row.status === 'mastered' ? 'warning' : 'success'"
              @click.stop="toggleMastered(scope.row)">
              {{ scope.row && scope.row.status === 'mastered' ? '取消掌握' : '标记掌握' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[12, 24, 48, 96]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total">
      </el-pagination>
    </div>

    <!-- 图片预览模态框 -->
    <el-dialog
      :visible.sync="imagePreviewVisible"
      width="80%"
      center
      :show-close="false"
      @click.native="imagePreviewVisible = false">
      <img :src="previewImageUrl" style="width: 100%; max-height: 70vh; object-fit: contain;" />
    </el-dialog>

    <!-- 错题详情模态框 -->
    <el-dialog
      title="错题详情"
      :visible.sync="detailVisible"
      width="900px"
      center>
      <div v-if="currentError" class="error-detail">
        <div class="detail-content">
          <div class="detail-left">
            <div class="detail-image">
              <img :src="currentError.imageUrl" :alt="currentError.subject" />
            </div>
          </div>
          <div class="detail-right">
            <div class="detail-info">
              <div class="info-item">
                <label>学科：</label>
                <span>{{ currentError.subject }}</span>
              </div>
              <div class="info-item">
                <label>知识点：</label>
                <span>{{ currentError.knowledgePoint }}</span>
              </div>
              <div class="info-item">
                <label>添加时间：</label>
                <span>{{ formatTime(currentError.createTime) }}</span>
              </div>
              <div class="info-item">
                <label>掌握状态：</label>
                <el-tag 
                  :type="currentError && currentError.status === 'mastered' ? 'success' : 'danger'"
                  size="small">
                  {{ currentError && currentError.status === 'mastered' ? '已掌握' : '未掌握' }}
                </el-tag>
              </div>
            </div>
            
            <div class="detail-answer" v-if="currentError.answer">
              <h4>解析：</h4>
              <div class="answer-content" v-html="currentError.answer"></div>
            </div>
          </div>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="primary" @click="practiceAgain(currentError)">重做练习</el-button>
        <el-button 
          :type="currentError && currentError.status === 'mastered' ? 'warning' : 'success'"
          @click="toggleMastered(currentError)">
          {{ currentError && currentError.status === 'mastered' ? '取消掌握' : '标记掌握' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { axios } from "../../config/axios";
import moment from 'moment';

export default {
  name: 'ErrorBook',
  data() {
    return {
      // 统计数据
      totalErrors: 0,
      masteredErrors: 0,
      unmasteredErrors: 0,
      
      // 筛选条件
      selectedSubject: '',
      selectedKnowledge: '',
      selectedStatus: '',
      dateRange: [],
      
      // 选项数据
      subjects: [
        { label: '数学', value: 'math' },
        { label: '语文', value: 'chinese' },
        { label: '英语', value: 'english' },
        { label: '物理', value: 'physics' },
        { label: '化学', value: 'chemistry' }
      ],
      knowledgePoints: [],
      
      // 视图模式
      viewMode: 'grid', // grid | list
      
      // 错题数据
      errorList: [],
      selectedErrorIds: [],
      loading: false,
      
      // 分页
      currentPage: 1,
      pageSize: 12,
      total: 0,
      
      // 模态框
      imagePreviewVisible: false,
      previewImageUrl: '',
      detailVisible: false,
      currentError: null
    }
  },
  created() {
    this.loadErrorBook();
    this.loadKnowledgePoints();
  },
  methods: {
    async loadErrorBook() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          subject: this.selectedSubject,
          knowledgePoint: this.selectedKnowledge,
          status: this.selectedStatus
        };
        
        if (this.dateRange && this.dateRange.length === 2) {
          params.startDate = moment(this.dateRange[0]).format('YYYY-MM-DD');
          params.endDate = moment(this.dateRange[1]).format('YYYY-MM-DD');
        }
        
        const response = await axios.get('/student/error-book', { params });
        const data = response.data.data;
        
        // 过滤并确保数据完整性
        this.errorList = (data.list || []).filter(item => item && item.id).map(item => ({
          ...item,
          status: item.status || 'unmastered',
          subject: item.subject || '未知',
          knowledgePoint: item.knowledgePoint || '未知',
          imageUrl: item.imageUrl || '/img/error-1.png',
          createTime: item.createTime || new Date()
        }));
        this.total = data.total || 0;
        this.totalErrors = data.totalErrors || 0;
        this.masteredErrors = data.masteredErrors || 0;
        this.unmasteredErrors = data.unmasteredErrors || 0;
        
      } catch (error) {
        console.error('加载错题本失败:', error);
        // 使用默认数据
        this.errorList = [
          {
            id: '1',
            subject: '数学',
            knowledgePoint: '函数与方程',
            imageUrl: '/img/error-1.png',
            status: 'unmastered',
            createTime: new Date(),
            answer: '这道题考查函数与方程的基础知识...'
          },
          {
            id: '2',
            subject: '英语',
            knowledgePoint: '阅读理解',
            imageUrl: '/img/error-2.png',
            status: 'mastered',
            createTime: new Date(),
            answer: '这道题考查阅读理解能力...'
          }
        ];
        this.total = 25;
        this.totalErrors = 25;
        this.masteredErrors = 8;
        this.unmasteredErrors = 17;
        this.$message.error('加载错题本失败，显示默认数据');
      } finally {
        this.loading = false;
      }
    },
    
    async loadKnowledgePoints() {
      try {
        const response = await axios.get('/student/knowledge-points', {
          params: { subject: this.selectedSubject }
        });
        this.knowledgePoints = response.data.data || [];
      } catch (error) {
        console.error('加载知识点失败:', error);
        // 使用默认知识点数据
        this.knowledgePoints = [
          { label: '函数与方程', value: '函数与方程' },
          { label: '三角函数', value: '三角函数' },
          { label: '数列', value: '数列' },
          { label: '立体几何', value: '立体几何' }
        ];
        this.$message.warning('加载知识点失败，显示默认数据');
      }
    },
    
    // 筛选处理
    handleSubjectChange() {
      this.selectedKnowledge = '';
      this.loadKnowledgePoints();
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    handleKnowledgeChange() {
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    handleStatusChange() {
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    handleDateChange() {
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    // 分页处理
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadErrorBook();
    },
    
    // 选择处理
    selectError(error) {
      this.currentError = error;
    },
    
    toggleSelection(errorId) {
      const index = this.selectedErrorIds.indexOf(errorId);
      if (index > -1) {
        this.selectedErrorIds.splice(index, 1);
      } else {
        this.selectedErrorIds.push(errorId);
      }
    },
    
    handleSelectionChange(selection) {
      this.selectedErrorIds = selection.map(item => item.id);
    },
    
    // 操作处理
    async toggleMastered(error) {
      if (!error || !error.id) {
        this.$message.error('错题数据无效');
        return;
      }
      
      try {
        const currentStatus = error.status || 'unmastered';
        const newStatus = currentStatus === 'mastered' ? 'unmastered' : 'mastered';
        await axios.put(`/student/error-book/${error.id}/status`, {
          status: newStatus
        });
        
        error.status = newStatus;
        this.$message.success(newStatus === 'mastered' ? '已标记为掌握' : '已取消掌握');
        
        // 更新统计数据
        if (newStatus === 'mastered') {
          this.masteredErrors++;
          this.unmasteredErrors--;
        } else {
          this.masteredErrors--;
          this.unmasteredErrors++;
        }
        
      } catch (error) {
        console.error('更新掌握状态失败:', error);
        this.$message.error('操作失败');
      }
    },
    
    async batchMarkMastered() {
      try {
        await axios.put('/student/error-book/batch-mastered', {
          errorIds: this.selectedErrorIds
        });
        
        // 更新本地数据
        this.errorList.forEach(error => {
          if (this.selectedErrorIds.includes(error.id) && error.status !== 'mastered') {
            error.status = 'mastered';
            this.masteredErrors++;
            this.unmasteredErrors--;
          }
        });
        
        this.selectedErrorIds = [];
        this.$message.success('批量标记成功');
        
      } catch (error) {
        console.error('批量标记失败:', error);
        this.$message.error('批量标记失败');
      }
    },
    
    async batchPrint() {
      try {
        await axios.post('/student/error-book/batch-print', {
          errorIds: this.selectedErrorIds
        });
        
        this.$message.success('打印任务已提交');
        this.selectedErrorIds = [];
        
      } catch (error) {
        console.error('批量打印失败:', error);
        this.$message.error('打印失败');
      }
    },
    
    practiceAgain(error) {
      this.$router.push(`/student/practice/${error.id}`);
    },
    
    viewDetail(error) {
      this.currentError = error;
      this.detailVisible = true;
    },
    
    viewImage(imageUrl) {
      this.previewImageUrl = imageUrl;
      this.imagePreviewVisible = true;
    },
    
    formatTime(time) {
      return moment(time).format('YYYY-MM-DD HH:mm');
    }
  }
}
</script>

<style lang="less" scoped>
.error-book {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

// 页面头部
.page-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

.header-stats {
  display: flex;
  gap: 32px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 28px;
  font-weight: 700;
  color: #3498db;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #7f8c8d;
}

// 筛选栏
.filter-bar {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-left {
  display: flex;
  gap: 16px;
  align-items: center;
}

.filter-right {
  display: flex;
  gap: 12px;
  align-items: center;
}

.view-toggle {
  margin-left: 12px;
}

// 网格视图
.error-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.error-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }
  
  &.selected {
    border: 2px solid #3498db;
  }
  
  &.mastered {
    opacity: 0.8;
    
    .error-image::after {
      content: '已掌握';
      position: absolute;
      top: 8px;
      right: 8px;
      background: #27ae60;
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
    }
  }
}

.card-checkbox {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
}

.error-image {
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
    
    i {
      color: white;
      font-size: 24px;
    }
  }
  
  &:hover .image-overlay {
    opacity: 1;
  }
}

.error-info {
  margin-bottom: 12px;
}

.error-subject {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.error-knowledge {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 4px;
}

.error-time {
  font-size: 12px;
  color: #95a5a6;
  margin-bottom: 8px;
}

.error-status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  &.mastered {
    background-color: #d4edda;
    color: #155724;
  }
  
  &.unmastered {
    background-color: #f8d7da;
    color: #721c24;
  }
}

.error-actions {
  display: flex;
  gap: 8px;
  
  .el-button {
    flex: 1;
    margin: 0;
  }
}

// 列表视图
.error-list {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.table-image {
  width: 80px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// 分页
.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 20px;
}

// 错题详情
.error-detail {
  .detail-content {
    display: flex;
    gap: 24px;
  }
  
  .detail-left {
    flex: 1;
  }
  
  .detail-right {
    flex: 1;
  }
  
  .detail-image {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: auto;
    }
  }
  
  .detail-info {
    margin-bottom: 24px;
  }
  
  .info-item {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    
    label {
      font-weight: 500;
      color: #2c3e50;
      min-width: 80px;
    }
  }
  
  .detail-answer {
    h4 {
      margin-bottom: 12px;
      color: #2c3e50;
    }
    
    .answer-content {
      padding: 16px;
      background-color: #f8f9fa;
      border-radius: 8px;
      line-height: 1.6;
    }
  }
}

// 响应式设计
@media (max-width: 1200px) {
  .error-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .error-book {
    padding: 15px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .header-stats {
    gap: 24px;
  }
  
  .filter-bar {
    flex-direction: column;
    gap: 16px;
  }
  
  .filter-left {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .error-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
  
  .detail-content {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .error-grid {
    grid-template-columns: 1fr;
  }
  
  .header-stats {
    flex-direction: column;
    gap: 16px;
  }
  
  .filter-left {
    width: 100%;
    
    .el-select, .el-date-editor {
      width: 100%;
      margin-bottom: 8px;
    }
  }
}

// 加载状态
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  background: white;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  
  .loading-text {
    color: #7f8c8d;
    font-size: 16px;
    
    i {
      margin-right: 8px;
      animation: rotating 2s linear infinite;
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// 文本颜色类
.text-success {
  color: #67c23a !important;
}

.text-warning {
  color: #e6a23c !important;
}
</style>
